import { Box, Grid, Tooltip, Typography } from '@mui/material';
import Paper from '@mui/material/Paper';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import { visuallyHidden } from '@mui/utils';
import { TFunction } from 'i18next';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import AlertCircle from '../../../components/AlertCircle';
import {
  MenuItemData,
  ThreeDotsMenu,
} from '../../../components/documented/ThreeDotsMenu';
import NoData from '../../../components/NoData';
import PathCircularProgress from '../../../components/PathCircularProgress';
import SearchBar from '../../../components/SearchBar/SearchBar';
import {
  calculateComplianceFill,
  renderAlertFill,
} from '../../../helpers/calculateFill/calculateFill';
import { useClientTheme } from '../../../helpers/hooks/useClientTheme';
import { ComplianceMode } from '../../../model/compliance/ComplianceMode';
import { Patient } from '../../../model/patient/patient';
import { clearSelectedDailyMetrics } from '../../../redux/actions/dailyMetricActions';
import { getPatients } from '../../../redux/actions/healthcareProfessionalActions';
import { useAppThunkDispatch } from '../../../redux/configureStore';
import { selectAccountId } from '../../../redux/selectors/accountSelectors';
import { selectPatients } from '../../../redux/selectors/patientSelectors';
import { healthcareProfessionalService } from '../../../services/healthcareProfessionalService';
import {
  copyIdToClipboard,
  getComparator,
  Order,
  StyledTableCell,
  StyledTableRow,
} from '../TableComponents/TableComponents';

const TableDetailContainer = styled(Box)(() => ({
  height: 'fit-content',
  width: '100%',
  padding: '10px 20px 10px 20px',
}));

export interface HeadCell {
  id: keyof Patient;
  label: string;
  numeric: boolean;
  toolTip?: any;
  visible: boolean;
}

export interface EnhancedTableProps {
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof Patient
  ) => void;
  order: Order;
  orderBy: string;
}

function renderComplianceTooltip(t: TFunction, theme: any, fill?: string) {
  const complianceCircles = [
    {
      fill: theme.palette.complianceUndefined.main,
      text: t('compliance.undefined'),
    },
    {
      fill: theme.palette.complianceNotAchievable.main,
      text: t('compliance.notAchievable'),
    },
    {
      fill: theme.palette.complianceAtRisk.main,
      text: t('compliance.atRisk'),
    },
    {
      fill: theme.palette.complianceOnTrack.main,
      text: t('compliance.onTrack'),
    },
    {
      fill: theme.palette.complianceCompleted.main,
      text: t('compliance.completed'),
    },
  ];

  if (fill) {
    const circle = complianceCircles.find((circle) => circle.fill === fill);
    return circle ? circle.text : '';
  }

  return (
    <Grid container>
      <Grid size={12} sx={{ mt: 0.5 }}>
        {t('compliance.intro')}
      </Grid>
      {complianceCircles.map((circle, index) => (
        <React.Fragment key={index}>
          <Grid size={3}


            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              margin: '5px 0',
            }}
          >
            <AlertCircle fill={circle.fill} />
          </Grid>
          <Grid size={7} style={{ margin: '5px 0' }}>
            {circle.text}
          </Grid>
        </React.Fragment>
      ))}
    </Grid>
  );
}

function renderAlertTooltip(t: TFunction, theme: any, fill?: string) {
  const complianceCircles = [
    {
      key: 1,
      fill: theme.palette.alertLevel1.main,
      text: t('alerts.level', { level: 1 }),
    },
    {
      key: 2,
      fill: theme.palette.alertLevel2.main,
      text: t('alerts.level', { level: 2 }),
    },
    {
      key: 3,
      fill: theme.palette.alertLevel3.main,
      text: t('alerts.level', { level: 3 }),
    },
  ];

  if (fill) {
    const circle = complianceCircles.find((circle) => circle.fill === fill);
    return circle ? circle.text : '';
  }

  return (
    <Grid container>
      <Grid size={12} sx={{ mt: 0.5 }}>
        {t('alerts.intro')}
      </Grid>
      {complianceCircles.map((circle, index) => (
        <React.Fragment key={index}>
          <Grid size={3}


            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              margin: '5px 0',
            }}
          >
            <AlertCircle fill={circle.fill} />
          </Grid>
          <Grid size={7} style={{ margin: '5px 0' }}>
            {circle.text}
          </Grid>
        </React.Fragment>
      ))}
    </Grid>
  );
}

function AuthorisedPatientsTableHead(props: EnhancedTableProps) {
  const { theme } = useClientTheme();
  const { t } = useTranslation(['patients', 'common']);
  const { order, orderBy, onRequestSort } = props;
  const complianceMode = import.meta.env
    .VITE_APP_COMPLIANCE_MODE as ComplianceMode;

  const createSortHandler =
    (property: keyof Patient) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  const headCells: readonly HeadCell[] = [
    {
      id: 'firstName',
      numeric: false,
      label: t('common:fields.firstName'),
      visible: true,
    },
    {
      id: 'lastName',
      numeric: false,
      label: t('common:fields.lastName'),
      visible: true,
    },
    {
      id: 'currentCompliancePeriod',
      numeric: true,
      label: t('authorised.daysOfUseCurrentPeriod'),
      toolTip: renderComplianceTooltip(t, theme),
      visible: complianceMode === ComplianceMode.CurrentPeriod,
    },
    {
      id: 'totalDaysOfInsoleUsageLastMonth',
      numeric: true,
      label: t('authorised.daysOfUsePreviousMonth'),
      toolTip: t('authorised.previousMonthTooltip'),
      visible: complianceMode === ComplianceMode.CurrentAndLastMonth,
    },
    {
      id: 'totalDaysOfInsoleUsageCurrentMonth',
      numeric: true,
      label: t('authorised.daysOfUseCurrentMonth'),
      toolTip: t('authorised.currentMonthTooltip'),
      visible: complianceMode === ComplianceMode.CurrentAndLastMonth,
    },
    {
      id: 'insoleLastUsageDate',
      numeric: false,
      label: t('authorised.lastDayOfUse'),
      visible: true,
    },
    {
      id: 'email',
      numeric: false,
      label: t('common:fields.email'),
      visible: true,
    },
    {
      id: 'alertLevel',
      numeric: true,
      label: t('authorised.alert'),
      toolTip: renderAlertTooltip(t, theme),
      visible: true,
    },
    {
      id: 'verified',
      numeric: false,
      label: t('authorised.insolesUserSince', {
        product: theme.productNames.insoles,
      }),
      visible: true,
    },
  ];

  return (
    <TableHead>
      <TableRow>
        {headCells
          .filter((headCell) => headCell.visible)
          .map((headCell) => (
            <StyledTableCell
              key={headCell.id}
              align={headCell.id !== 'alertLevel' ? 'left' : 'center'}
              sortDirection={orderBy === headCell.id ? order : false}
            >
              <Tooltip title={headCell.toolTip || ''}>
                <TableSortLabel
                  active={orderBy === headCell.id}
                  direction={orderBy === headCell.id ? order : 'asc'}
                  onClick={createSortHandler(headCell.id)}
                >
                  {headCell.label}
                  {orderBy === headCell.id ? (
                    <Box component="span" sx={visuallyHidden}>
                      {order === 'desc'
                        ? t('common:table.sortedDescending')
                        : t('common:table.sortedAscending')}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </Tooltip>
            </StyledTableCell>
          ))}
        <StyledTableCell align="left">
          {t('common:table.userActions')}
        </StyledTableCell>
      </TableRow>
    </TableHead>
  );
}

interface Props {
  isLoading: boolean;
}

export const AuthorisedPatientsTable: React.FC<Props> = ({ isLoading }) => {
  const { t } = useTranslation(['patients', 'common']);
  const patients = useSelector(selectPatients);
  const accountId = useSelector(selectAccountId);
  const navigate = useNavigate();
  const dispatch = useAppThunkDispatch();
  const { theme } = useClientTheme();
  const complianceMode = import.meta.env
    .VITE_APP_COMPLIANCE_MODE as ComplianceMode;

  const [order, setOrder] = React.useState<Order>('asc');
  const [orderBy, setOrderBy] = React.useState<keyof Patient>('lastName');
  const [searchTerm, setSearchTerm] = React.useState('');

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof Patient
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  const filteredPatients = React.useMemo(() => {
    try {
      // If no search term, return all patients
      if (!searchTerm) return patients;

      // Validate patients array
      if (!patients || !Array.isArray(patients) || patients.length === 0) {
        return [];
      }

      // Get lowercase search term
      const searchLower = String(searchTerm).toLowerCase();

      // Filter patients with error handling for each one
      return patients.filter((patient) => {
        try {
          // Return false for any null or undefined patient
          if (!patient) return false;

          // Get patient properties safely
          const firstName =
            typeof patient.firstName === 'string'
              ? patient.firstName.toLowerCase()
              : '';
          const lastName =
            typeof patient.lastName === 'string'
              ? patient.lastName.toLowerCase()
              : '';
          const email =
            typeof patient.email === 'string'
              ? patient.email.toLowerCase()
              : '';
          const fullName =
            `${typeof patient.firstName === 'string' ? patient.firstName : ''} ${typeof patient.lastName === 'string' ? patient.lastName : ''}`.toLowerCase();

          // Check if search term is in any of the fields
          return (
            firstName.includes(searchLower) ||
            lastName.includes(searchLower) ||
            email.includes(searchLower) ||
            fullName.includes(searchLower)
          );
        } catch (err) {
          // If there's any error processing this patient, exclude it from results
          console.error('Error filtering patient:', err);
          return false;
        }
      });
    } catch (err) {
      // If there's any error in the overall filtering process, return empty array
      console.error('Error filtering patients:', err);
      return [];
    }
  }, [patients, searchTerm]);

  const handleClick = (
    event: React.MouseEvent<unknown>,
    accountGuid: string
  ) => {
    const selectedPatientData = patients.find(
      (data) => data.accountGuid === accountGuid
    );
    window.localStorage.setItem(
      'selectedPatientFromPatientsTable',
      JSON.stringify(selectedPatientData)
    );
    dispatch(clearSelectedDailyMetrics());
    navigate(`/dashboard/patients/${accountGuid}`);
  };

  const handleRemovePatient = (patientId: string) => {
    healthcareProfessionalService
      .revokePatient(accountId, patientId)
      .then((response: any) => {
        dispatch(getPatients(accountId, true));
      })
      .catch((error: any) => {});
  };

  const menuItems: MenuItemData[] = [
    {
      label: t('common:actions.copyGuid'),
      action: (data) => copyIdToClipboard(data.patientId),
    },
    {
      label: t('common:actions.remove'),
      action: (data) => handleRemovePatient(data.patientId),
    },
  ];

  return (
    <TableDetailContainer>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 2,
        }}
      >
        <Typography
          sx={{ padding: '5px 0px' }}
          variant="h5"
          gutterBottom
          component="h2"
        >
          {t('authorised.title')}
        </Typography>

        {!isLoading && patients.length > 0 && (
          <SearchBar
            placeholder={t('authorised.searchPlaceholder')}
            value={searchTerm}
            onChange={handleSearchChange}
          />
        )}
      </Box>

      {!isLoading ? (
        filteredPatients.length ? (
          <Paper sx={{ width: '100%', mb: 2 }}>
            <TableContainer sx={{ maxHeight: 980, minWidth: 750 }}>
              <Table stickyHeader aria-labelledby="authorised-patients-table">
                <AuthorisedPatientsTableHead
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleRequestSort}
                />
                <TableBody>
                  {filteredPatients
                    .slice()
                    .sort(getComparator(order, orderBy))
                    .map((patient) => (
                      <StyledTableRow
                        hover
                        tabIndex={-1}
                        key={patient.accountGuid}
                      >
                        <StyledTableCell
                          align="left"
                          onClick={(event) =>
                            handleClick(event, patient.accountGuid)
                          }
                        >
                          {patient.firstName}
                        </StyledTableCell>
                        <StyledTableCell
                          align="left"
                          onClick={(event) =>
                            handleClick(event, patient.accountGuid)
                          }
                        >
                          {patient.lastName}
                        </StyledTableCell>

                        {complianceMode === ComplianceMode.CurrentPeriod && (
                          <StyledTableCell
                            style={{ width: '300px' }}
                            align="left"
                            onClick={(event) =>
                              handleClick(event, patient.accountGuid)
                            }
                          >
                            {patient.currentCompliancePeriod ? (
                              <Grid container>
                                <Grid size={3}


                                  style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                  }}
                                >
                                  <Tooltip
                                    title={renderComplianceTooltip(
                                      t,
                                      theme,
                                      calculateComplianceFill(
                                        theme,
                                        patient.currentCompliancePeriod
                                          .daysOfUse,
                                        patient.currentCompliancePeriod
                                          .daysTotal
                                      )
                                    )}
                                  >
                                    <AlertCircle
                                      data-testid="compliance-circle"
                                      fill={calculateComplianceFill(
                                        theme,
                                        patient.currentCompliancePeriod
                                          .daysOfUse,
                                        patient.currentCompliancePeriod
                                          .daysTotal
                                      )}
                                    />
                                  </Tooltip>
                                </Grid>
                                <Grid size={7}>
                                  {t('authorised.complianceSummary', {
                                    used: patient.currentCompliancePeriod
                                      ?.daysOfUse,
                                    total:
                                      patient.currentCompliancePeriod.daysTotal,
                                  })}
                                </Grid>
                                <Grid size={12} sx={{ mt: 0.5 }}>
                                  {t('authorised.complianceEndingOn', {
                                    date: new Date(
                                      patient.currentCompliancePeriod.endTime
                                    ).toDateString(),
                                  })}
                                </Grid>
                              </Grid>
                            ) : null}
                          </StyledTableCell>
                        )}

                        {complianceMode ===
                          ComplianceMode.CurrentAndLastMonth && (
                          <>
                            <StyledTableCell
                              align="left"
                              onClick={(event) =>
                                handleClick(event, patient.accountGuid)
                              }
                            >
                              {t('authorised.daysOfUse', {
                                count:
                                  patient.totalDaysOfInsoleUsageLastMonth,
                              })}
                            </StyledTableCell>
                            <StyledTableCell
                              align="left"
                              onClick={(event) =>
                                handleClick(event, patient.accountGuid)
                              }
                            >
                              {t('authorised.daysOfUse', {
                                count:
                                  patient.totalDaysOfInsoleUsageCurrentMonth,
                              })}
                            </StyledTableCell>
                          </>
                        )}

                        <StyledTableCell
                          align="left"
                          onClick={(event) =>
                            handleClick(event, patient.accountGuid)
                          }
                        >
                          {new Date(patient.insoleLastUsageDate).toDateString()}
                        </StyledTableCell>
                        <StyledTableCell
                          align="left"
                          onClick={(event) =>
                            handleClick(event, patient.accountGuid)
                          }
                        >
                          {patient.email}
                        </StyledTableCell>
                        <StyledTableCell
                          align="center"
                          onClick={(event) =>
                            handleClick(event, patient.accountGuid)
                          }
                        >
                          {patient.alertLevel && patient.alertLevel > 0 ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mr: '18px' }}>
                              <Tooltip
                                title={renderAlertTooltip(
                                  t,
                                  theme,
                                  renderAlertFill(theme, patient.alertLevel)
                                )}
                              >
                                <AlertCircle
                                  data-testid="alert-circle"
                                  fill={renderAlertFill(
                                    theme,
                                    patient.alertLevel
                                  )}
                                />
                              </Tooltip>
                            </Box>
                          ) : null}
                        </StyledTableCell>
                        <StyledTableCell
                          align="left"
                          onClick={(event) =>
                            handleClick(event, patient.accountGuid)
                          }
                        >
                          {new Date(patient.verified).toDateString()}
                        </StyledTableCell>
                        <StyledTableCell align="left">
                          <ThreeDotsMenu
                            data={{ patientId: patient.accountGuid, accountId }}
                            menuItems={menuItems}
                          />
                        </StyledTableCell>
                      </StyledTableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        ) : searchTerm ? (
          <NoData
            message={t('authorised.noneMatching', { term: searchTerm })}
          />
        ) : (
          <NoData message={t('authorised.empty')} />
        )
      ) : (
        <Box
          data-testid="loading-indicator" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100px', justifyContent: 'center' }}>
          <PathCircularProgress />
        </Box>
      )}
    </TableDetailContainer>
  );
};

export default AuthorisedPatientsTable;
